import React from 'react';
import { BarChart3, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * ReliabilityPanel — admin dashboard showing how often users hit
 * "Open in {service}" fallback links (a signal that the embedded player
 * misbehaved). Pure presentation: parent owns the stats payload + the
 * window-size selector + refresh handler.
 *
 * Extracted from AdminSettings.jsx (2026-05-30) to bring that page
 * below the 800-line health threshold.
 */
const ReliabilityPanel = ({
  stats,
  days,
  loading,
  onDaysChange,
  onRefresh,
}) => {
  return (
    <div className="mt-8 bg-white/5 rounded-xl border border-white/10 p-6" data-testid="reliability-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center">
          <BarChart3 className="h-6 w-6 text-rose-500 mr-2" />
          <h2 className="text-xl font-bold text-white">Embed Reliability</h2>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-400">Window:</label>
          <select
            value={days}
            onChange={(e) => onDaysChange(parseInt(e.target.value, 10))}
            className="bg-zinc-900 border border-white/10 text-white text-sm rounded-md px-2 py-1"
            data-testid="reliability-days"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button
            onClick={onRefresh}
            disabled={loading}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="refresh-reliability-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
          </Button>
        </div>
      </div>
      <p className="text-gray-400 text-sm mb-4">
        Each row counts how many times users clicked an "Open in {'{Service}'}" fallback link — a strong signal that the embedded player misbehaved. Sort to spot which service × browser combinations are flakiest, and use the per-book "Force Open-in-{'{Service}'} Mode" toggle (in each book's Edit Bio & Series modal) to bypass problematic embeds.
      </p>
      {stats?.rows?.length === 0 ? (
        <div className="text-center py-8 text-gray-500" data-testid="reliability-empty">
          No fallback events in the last {stats?.days || days} days. Embeds are behaving well!
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="reliability-table">
            <thead>
              <tr className="text-left text-gray-400 border-b border-white/10">
                <th className="py-2 px-3">Service</th>
                <th className="py-2 px-3">Browser</th>
                <th className="py-2 px-3 text-right">Failures</th>
                <th className="py-2 px-3">Last seen</th>
                <th className="py-2 px-3">Sample Page</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.rows || []).map((r) => (
                <tr key={`${r.service}-${r.browser}`} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-2 px-3">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                      r.service === 'youtube' ? 'bg-red-500/20 text-red-300' :
                      r.service === 'spotify' ? 'bg-green-500/20 text-green-300' :
                      'bg-orange-500/20 text-orange-300'
                    }`}>{r.service}</span>
                  </td>
                  <td className="py-2 px-3 text-gray-200">{r.browser}</td>
                  <td className="py-2 px-3 text-right">
                    <span className="font-bold text-rose-400">{r.count}</span>
                  </td>
                  <td className="py-2 px-3 text-gray-500 text-xs">
                    {r.last_seen ? new Date(r.last_seen).toLocaleString() : '—'}
                  </td>
                  <td className="py-2 px-3 text-gray-500 text-xs truncate max-w-[200px]" title={r.sample_path}>
                    {r.sample_path || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-600 mt-3" data-testid="reliability-total">
            {stats?.total_events || 0} total events in last {stats?.days || days} days.
          </p>
        </div>
      )}
    </div>
  );
};

export default ReliabilityPanel;
